<script setup>
/* global axios */
// Quick Start — inicia uma conversa só pelo telefone (sem cadastrar contato à
// mão). Botão no header da lista de conversas que abre um popover com telefone +
// caixa + mensagem inicial. Porta o SCRIPT 6 do dashboard injetado.
import { ref, computed, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const accountId = useMapGetter('getCurrentAccountId');
const inboxesGetter = useMapGetter('inboxes/getInboxes');
const inboxes = computed(() => inboxesGetter.value || []);

const open = ref(false);
const phone = ref('');
const inboxId = ref('');
const message = ref('');
const saving = ref(false);
const phoneInput = ref(null);

// Autocomplete de contatos: a busca do backend cobre nome, e-mail e telefone,
// então tanto faz digitar dígitos ou letras. Usa a API direto (e não o store de
// contatos) para não sobrescrever a lista que a tela de Contatos mantém.
const matches = ref([]);
const pickedName = ref('');
const activeIndex = ref(-1);

const clearSuggestions = () => {
  matches.value = [];
  activeIndex.value = -1;
};

const searchContacts = debounce(async () => {
  const term = phone.value.trim();
  if (term.length < 3) {
    clearSuggestions();
    return;
  }
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/search`,
      { params: { q: term, page: 1 } }
    );
    matches.value = (data?.payload || [])
      .filter(c => c.phone_number)
      .slice(0, 5);
    activeIndex.value = -1;
  } catch (e) {
    clearSuggestions();
  }
}, 300);

// setas percorrem a lista (circular); Enter escolhe o item destacado ou, se
// nenhum estiver, segue para o envio normal.
const move = delta => {
  const total = matches.value.length;
  if (!total) return;
  activeIndex.value = (activeIndex.value + delta + total) % total;
};

const onPhoneInput = () => {
  pickedName.value = '';
  searchContacts();
};

// O @click.stop no item é necessário: a diretiva de clique-fora escuta no
// document e testa el.contains(event.target). Limpar as sugestões remove o
// botão clicado do DOM antes do evento subir, o contains passa a dar false e o
// popover fechava sozinho ao escolher um contato. Sem propagar, isso não ocorre.
const pickContact = contact => {
  phone.value = contact.phone_number;
  pickedName.value = contact.name;
  clearSuggestions();
  phoneInput.value?.focus();
};

// Caixa unica: seleciona sozinha. Escolher entre uma opcao so e atrito sem
// informacao, e o formulario fica incompleto ate alguem clicar no obvio.
// Com duas ou mais a escolha continua explicita — iniciar pela caixa errada
// significa falar com o cliente por outro numero.
// Watcher (e nao atribuicao no toggle) porque a lista chega por dispatch
// assincrono: no momento em que o popover abre ela costuma estar vazia.
watch(
  inboxes,
  list => {
    if (!inboxId.value && list.length === 1) inboxId.value = list[0].id;
  },
  { immediate: true }
);

const toggle = async () => {
  open.value = !open.value;
  if (open.value) {
    if (!inboxes.value.length) store.dispatch('inboxes/get');
    await nextTick();
    phoneInput.value?.focus();
  } else {
    clearSuggestions();
  }
};

const close = () => {
  open.value = false;
  clearSuggestions();
};

const submit = async () => {
  const digits = phone.value.replace(/\D/g, '');
  if (digits.length < 8) {
    useAlert(t('CONVERSATION.QUICK_START.INVALID_PHONE'));
    return;
  }
  if (!inboxId.value) {
    useAlert(t('CONVERSATION.QUICK_START.SELECT_INBOX'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/kanban/quick_start`,
      { phone: digits, inbox_id: inboxId.value, content: message.value.trim() }
    );
    open.value = false;
    phone.value = '';
    message.value = '';
    router.push(
      frontendURL(
        `accounts/${accountId.value}/conversations/${data.conversation_id}`
      )
    );
  } catch (e) {
    const d = e?.response?.data;
    // 409: o contato já tem conversa não resolvida. A mensagem NÃO foi enviada.
    // Mostra o código e o responsável para quem clicou decidir — e mantém o
    // popover aberto com o texto digitado, para não perder o que a pessoa
    // escreveu enquanto ela decide.
    if (e?.response?.status === 409 && d?.conversation_id) {
      const dono = d.assigned_to_me
        ? 'você'
        : d.assignee_name || 'ninguém (sem responsável)';
      useAlert(
        `Não enviei: este contato já tem a conversa #${d.conversation_id} em aberto, ` +
          `atribuída a ${dono}.`
      );
      // popover segue aberto com o texto digitado, para não perder o que a
      // pessoa escreveu enquanto decide o que fazer
      return;
    }
    useAlert(d?.error || t('CONVERSATION.QUICK_START.ERROR'));
  } finally {
    saving.value = false;
  }
};

// Enter escolhe o item destacado pelas setas; sem destaque, segue para o envio.
const onEnter = () => {
  const highlighted = matches.value[activeIndex.value];
  if (highlighted) {
    pickContact(highlighted);
    return;
  }
  submit();
};
</script>

<template>
  <div v-on-clickaway="close" class="relative">
    <NextButton
      v-tooltip.bottom="t('CONVERSATION.QUICK_START.TOOLTIP')"
      icon="i-lucide-message-square-plus"
      slate
      xs
      faded
      @click="toggle"
    />
    <div
      v-if="open"
      class="absolute z-50 mt-2 w-72 p-3 rounded-xl shadow-lg border border-n-weak bg-n-solid-1 ltr:right-0 rtl:left-0 flex flex-col gap-2"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm font-semibold text-n-slate-12">{{
          t('CONVERSATION.QUICK_START.TITLE')
        }}</span>
        <button
          class="text-n-slate-10 hover:text-n-slate-12 text-lg leading-none"
          @click="close"
        >
          ×
        </button>
      </div>
      <div class="relative">
        <input
          ref="phoneInput"
          v-model="phone"
          type="tel"
          :placeholder="t('CONVERSATION.QUICK_START_PHONE_PLACEHOLDER')"
          autocomplete="off"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          @input="onPhoneInput"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="clearSuggestions"
        />
        <p
          v-if="pickedName"
          class="flex gap-1 items-center mt-1 text-xs text-n-teal-11"
        >
          <span class="i-lucide-check size-3" />
          <span>{{ t('CONVERSATION.QUICK_START.EXISTING_CONTACT') }}</span>
          <span class="font-medium">{{ pickedName }}</span>
        </p>
        <div
          v-if="matches.length"
          class="overflow-y-auto absolute z-10 mt-1 w-full max-h-48 rounded-lg border shadow-lg border-n-weak bg-n-solid-1"
        >
          <button
            v-for="(contact, index) in matches"
            :key="contact.id"
            type="button"
            class="flex flex-col gap-0.5 items-start px-3 py-2 w-full text-left hover:bg-n-alpha-1"
            :class="{ 'bg-n-alpha-2': index === activeIndex }"
            @mouseenter="activeIndex = index"
            @click.stop="pickContact(contact)"
          >
            <span class="text-sm truncate text-n-slate-12">
              {{ contact.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ contact.phone_number }}
            </span>
          </button>
        </div>
      </div>      <div class="relative">
        <select
          v-model="inboxId"
          class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('CONVERSATION.QUICK_START.SELECT_INBOX') }}
          </option>
          <option v-for="i in inboxes" :key="i.id" :value="i.id">
            {{ i.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
        />
      </div>
      <textarea
        v-model="message"
        rows="2"
        :placeholder="t('CONVERSATION.QUICK_START.MESSAGE_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 resize-none"
      />
      <NextButton
        :label="
          saving
            ? t('CONVERSATION.QUICK_START.CREATING')
            : t('CONVERSATION.QUICK_START.START')
        "
        blue
        sm
        :is-loading="saving"
        @click="submit"
      />
    </div>
  </div>
</template>
